import type { Header, RowData } from '@tanstack/react-table';
import groupBy from 'lodash-es/groupBy';
import sortBy from 'lodash-es/sortBy';
import { useMemo } from 'react';

import { List } from '@apple/ui/list/list';
import { ListItem } from '@apple/ui/list/list-item';

import type { DataTableFeatures } from '../features';
import { FilterInput } from './filters';

type Props<Data extends RowData> = {
	headers: Header<DataTableFeatures, Data, unknown>[];
	variant?: 'column' | 'row';
};

export function FiltersList<Data extends RowData>({
	headers = [],
	variant = 'column',
}: Props<Data>) {
	const groupedHeaders = useMemo(
		() =>
			groupBy(
				sortBy(
					headers.filter(x => x.column.getCanFilter()),
					x => x.column.columnDef.filter?.sortOrder,
				),
				x => x.column.columnDef.filter?.group ?? '',
			),
		[headers],
	);
	const filterLists = useMemo(
		() =>
			Object.entries(groupedHeaders).map(([grouping, headers]) => (
				<List key={grouping} title={grouping}>
					{headers.map(header => (
						<ListItem
							key={header.id}
							label={header.column.columnDef.filter?.label ?? getHeaderLabel(header)}
							htmlFor={header.id}
							column={variant === 'column'}
						>
							<FilterInput
								header={header}
								label={
									header.column.columnDef.filter?.label ?? getHeaderLabel(header)
								}
							/>
						</ListItem>
					))}
				</List>
			)),
		[groupedHeaders, variant],
	);

	return <>{filterLists}</>;
}

function getHeaderLabel<TData extends RowData, TValue>(
	header: Header<DataTableFeatures, TData, TValue>,
) {
	return (
		(typeof header.column.columnDef.header === 'function'
			? (header.column.columnDef.header(header.getContext()) as string)
			: header.column.columnDef.header) ?? header.column.id
	);
}
